import { useDestinationsContext } from '@/components/OrderWizard/Contexts/DestinationsContext'
import { useOrderContainersContext } from '@/components/OrderWizard/Contexts/OrderContainersContext'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useMemo } from 'react'
import { SupplyFilterSelect } from './SupplyFilterSelect'
import { SupplyOwnerDefinition } from '../types'
import { uniqBy } from 'lodash'
import { IconGasStation } from '@tabler/icons-react'
import { getDestinationProductsFromCompartments, getOptions } from '../util'
import { ProductGroupsApiType } from '@/components/OrderWizard/Contexts/OrderContainersContext/ProductGroups/useProductGroups'

type SupplyOwnerCollapseProps = {
  metadata: MetadataResponse | undefined
  selectedSupplyOwners: Record<string, SupplyOwnerDefinition> | undefined
  setSelectedSupplyOwners: React.Dispatch<React.SetStateAction<Record<string, SupplyOwnerDefinition> | undefined>>
  productGroups: ProductGroupsApiType['productGroups']
  isByProduct: boolean
}

export function SupplyOwnerCollapse({
  metadata,
  selectedSupplyOwners,
  setSelectedSupplyOwners,
  productGroups,
  isByProduct,
}: SupplyOwnerCollapseProps) {
  const { compartments } = useOrderContainersContext()
  const { orderDestinations } = useDestinationsContext()

  const supplyOwners = useMemo(() => {
    const metadataOwners = getOptions(metadata, 'supply_owners')
    const selectedDestOwners = orderDestinations.map((d) => ({ label: d.supply_owner_name, value: d.supply_owner_id }))
    return uniqBy([...metadataOwners, ...selectedDestOwners], 'value')
  }, [orderDestinations, metadata])
  const destinationProductCombos = useMemo(
    () => getDestinationProductsFromCompartments(compartments, productGroups, isByProduct),
    [compartments, productGroups, isByProduct]
  )

  function handleChange(locationName: string, product: string, supplyOwnerId: string) {
    const newDefinitions = { ...selectedSupplyOwners }
    const key = `${locationName}::${product}`
    if (newDefinitions[key]) {
      newDefinitions[key] = { ...newDefinitions[key], supply_owner_id: supplyOwnerId }
    }
    setSelectedSupplyOwners(newDefinitions)
  }

  return (
    <Vertical gap={8} className='mb-4'>
      {destinationProductCombos.map((combo, index) => (
        <SupplyFilterSelect
          key={index}
          title={
            <Horizontal verticalCenter gap={2}>
              <IconGasStation size={18} />
              <Texto weight={'bold'}>
                {combo.locationName} ({combo.tankIds.join(', ')})
              </Texto>
            </Horizontal>
          }
          right={<Texto>{combo.product}</Texto>}
          value={selectedSupplyOwners?.[`${combo.locationName}::${combo.product}`]?.supply_owner_id || null}
          options={supplyOwners}
          onChange={(value) => handleChange(combo.locationName, combo.product, value as string)}
          disabled={supplyOwners.length <= 1}
        />
      ))}
    </Vertical>
  )
}
